import React, { useState, useCallback } from 'react';
import { string, func, bool } from 'prop-types';
import classNames from 'classnames';
import { FormattedMessage } from '../../util/reactIntl';
import css from './InviteCodeField.module.css';

/**
 * InviteCodeField — invite code input for SignupForm.
 *
 * Validates the code against /api/invites/validate before allowing signup to proceed.
 *
 * Usage (in SignupForm, after existing fields):
 *   {features.userInvites && (
 *     <InviteCodeField onValidCode={code => setInviteCode(code)} />
 *   )}
 *
 * @param {Function} props.onValidCode — called with the valid code after validation
 * @param {boolean} [props.required] — if false, invite code is optional
 */
const InviteCodeField = ({ onValidCode, required, className }) => {
  const [code, setCode] = useState('');
  const [status, setStatus] = useState(null); // null | 'valid' | 'invalid'
  const [isLoading, setIsLoading] = useState(false);

  const validate = useCallback(async (value) => {
    const trimmed = value.trim().toUpperCase();
    if (!trimmed) {
      setStatus(null);
      return;
    }

    setIsLoading(true);
    try {
      const res = await fetch('/api/invites/validate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code: trimmed }),
      });
      const data = await res.json();
      if (data.valid) {
        setStatus('valid');
        onValidCode(trimmed);
      } else {
        setStatus('invalid');
        onValidCode(null);
      }
    } catch (_) {
      setStatus('invalid');
      onValidCode(null);
    } finally {
      setIsLoading(false);
    }
  }, [onValidCode]);

  return (
    <div className={classNames(css.root, className)}>
      <label className={css.label}>
        <FormattedMessage id="InviteCodeField.label" />
        {!required && (
          <span className={css.optional}>
            {' '}(<FormattedMessage id="InviteCodeField.optional" />)
          </span>
        )}
      </label>
      <div className={css.inputRow}>
        <input
          type="text"
          value={code}
          onChange={e => {
            setCode(e.target.value);
            setStatus(null);
          }}
          onBlur={e => validate(e.target.value)}
          placeholder="XXXX-XXXX"
          className={classNames(
            css.input,
            status === 'valid' && css.inputValid,
            status === 'invalid' && css.inputInvalid
          )}
        />
        {isLoading && <span className={css.spinner}>…</span>}
        {status === 'valid' && <span className={css.checkmark}>✓</span>}
      </div>
      {status === 'invalid' && (
        <p className={css.error}>
          <FormattedMessage id="InviteCodeField.invalid" />
        </p>
      )}
    </div>
  );
};

InviteCodeField.defaultProps = { required: true, className: null };
InviteCodeField.propTypes = { onValidCode: func.isRequired, required: bool, className: string };

export default InviteCodeField;
